/**
 * geo-utils.js — pure, dependency-free geospatial helpers for the GeoTracker
 * module. No browser APIs, no framework imports — safe to reuse anywhere
 * (including Node, web workers, or other projects).
 *
 * @module geotracker/geo-utils
 */

const EARTH_RADIUS_M = 6371008.8; // mean Earth radius (metres)
const DEG2RAD = Math.PI / 180;

/**
 * Great-circle distance between two lon/lat points using the haversine
 * formula.
 *
 * @param {number} lon1
 * @param {number} lat1
 * @param {number} lon2
 * @param {number} lat2
 * @returns {number} distance in metres
 */
export function haversineMeters(lon1, lat1, lon2, lat2) {
  const dLat = (lat2 - lat1) * DEG2RAD;
  const dLon = (lon2 - lon1) * DEG2RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG2RAD) * Math.cos(lat2 * DEG2RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Total length of a polyline expressed as an array of points.
 * @param {Array<{lon:number, lat:number}>} points
 * @returns {number} metres
 */
export function pathLengthMeters(points) {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineMeters(points[i - 1].lon, points[i - 1].lat, points[i].lon, points[i].lat);
  }
  return total;
}

/**
 * Format a latitude or longitude for compact display.
 * @param {number} value
 * @param {number} [decimals=5] ~1.1 m precision at 5 dp
 * @returns {string}
 */
export function formatCoord(value, decimals = 5) {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toFixed(decimals);
}

/**
 * Format a distance in metres into a friendly string (m below 1 km, km above).
 * @param {number} meters
 * @returns {string}
 */
export function formatDistance(meters) {
  if (meters == null || Number.isNaN(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

/**
 * Format a horizontal accuracy (metres) into a friendly ± string.
 * @param {number|null} meters
 * @returns {string}
 */
export function formatAccuracy(meters) {
  if (meters == null || Number.isNaN(meters)) return '—';
  return `±${Math.round(meters)} m`;
}

/**
 * Classify a horizontal accuracy into a qualitative fix-quality bucket. Useful
 * for colour-coding the UI without needing satellite count.
 * @param {number|null} meters
 * @returns {'good'|'fair'|'poor'|'none'}
 */
export function accuracyQuality(meters) {
  if (meters == null || Number.isNaN(meters)) return 'none';
  if (meters <= 10) return 'good';
  if (meters <= 30) return 'fair';
  return 'poor';
}
